/**
 * EDD Generation Flow endpoints (Part 2 — the flagship 5-step wizard).
 *
 * N work-orders × M formats through the existing deterministic engine. Wire shapes mirror
 * app/schemas/generate_flow.py. Inline types here (we do NOT touch the shared api/types.ts);
 * upload/email/format hooks are REUSED from their existing modules — this file only adds the
 * new flow preview / run / download / regenerate calls.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

/** A source descriptor: the literal "synthetic", or a one-key id wrapper (one per work order). */
export type FlowSource =
  | "synthetic"
  | { csv_zip_id: string }
  | { output_mdb_id: string };

/** One EDD format to generate: exactly one of config_id / template (a catalog template id). */
export interface FlowFormatSelector {
  config_id?: string;
  template?: string;
}

export interface FlowPreviewIn {
  sources: FlowSource[];
  formats: FlowFormatSelector[];
  output_formats?: string[];
  n_samples?: number;
  run_sql?: boolean;
  spec_id?: string | null;
  location_id?: string | null;
  max_rows?: number;
}

export interface FlowColumnProvenance {
  column: string;
  /** "OutTable.OutField" | "(static value)" | "(computed)". */
  source: string;
  field_type: string;
  static: boolean;
  hidden: boolean;
}

export interface FlowComboPreview {
  source_index: number;
  work_order: string;
  source_kind: string;
  config_id: string | null;
  template: string | null;
  config_name: string;
  output_format: string;
  columns: string[];
  rows: Record<string, string | number | boolean | null>[];
  sx_row_count: number;
  qa_row_count: number;
  provenance: FlowColumnProvenance[];
  validation: ValidationReport | null;
  warnings: string[];
  status: "ok" | "warnings" | "errors";
  error: string | null;
}

export interface ValidationIssue {
  code: string;
  severity: "error" | "warning";
  message: string;
  field?: string | null;
  row?: number | null;
  value?: string | null;
  suggestion?: string | null;
}

export interface ValidationReport {
  ok: boolean;
  spec_id?: string | null;
  spec_name?: string | null;
  summary: { errors: number; warnings: number; rows_checked: number; fields_checked: number };
  issues: ValidationIssue[];
}

export interface FlowRunIn {
  name?: string;
  sources: FlowSource[];
  formats: FlowFormatSelector[];
  output_formats?: string[];
  n_samples?: number;
  run_sql?: boolean;
  validate?: boolean;
  spec_id?: string | null;
  location_id?: string | null;
}

export interface FlowRunOut {
  id: string;
  name: string;
  kind: "flow";
  status: "completed" | "partial" | "failed";
  source_count: number;
  format_count: number;
  total_items: number;
  completed_items: number;
  failed_items: number;
  file_count: number;
  has_result: boolean;
  warnings: string[];
  created_at: string;
  updated_at: string;
}

export interface RegenerateIn {
  sources?: FlowSource[] | null;
  name?: string | null;
}

// ── hooks ────────────────────────────────────────────────────────────────────────
/** Step 3 — preview every (work order × format) combo. */
export function useFlowPreview() {
  return useMutation({
    mutationFn: async (payload: FlowPreviewIn): Promise<FlowComboPreview[]> => {
      const { data } = await api.post<FlowComboPreview[]>("/generate/flow/preview", payload);
      return data;
    },
  });
}

/** Step 4 — run the whole flow → one GenerationJob (kind="flow"). */
export function useRunFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: FlowRunIn): Promise<FlowRunOut> => {
      const { data } = await api.post<FlowRunOut>("/generate/flow", payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["generation-jobs"] });
    },
  });
}

/** Step 5 — re-run a prior flow job (same formats, optionally new sources). */
export function useRegenerateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { jobId: string; body: RegenerateIn }): Promise<FlowRunOut> => {
      const { data } = await api.post<FlowRunOut>(
        `/generate/flow/${args.jobId}/regenerate`,
        args.body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["generation-jobs"] });
    },
  });
}

// ── authenticated download (per-WO-folder zip; mirrors api/generation.ts downloadBlob) ──
function filenameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== "string") return fallback;
  const plain = /filename=["']?([^"';]+)["']?/i.exec(header);
  return plain?.[1] ?? fallback;
}

/** Download the combined flow deliverable (folder per work order, files per EDD). */
export async function downloadFlowBundle(jobId: string, name = "edd-deliverables"): Promise<void> {
  const res = await api.get<Blob>(`/generate/flow/${jobId}/download`, {
    responseType: "blob",
  });
  const fname = filenameFromDisposition(res.headers["content-disposition"], `${name}.zip`);
  const url = URL.createObjectURL(res.data);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
